import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "../components/Seo";
import useReveal from "../hooks/useReveal";
import usePublicData from "../hooks/usePublicData";
import { useEstimate } from "../components/EstimateContext";
import { COMPANY, TESTIMONIALS } from "../mock";
import { SERVICE_AREAS, AREA_SERVICES, findArea } from "../data/serviceAreas";
import { MapPin, ArrowRight, ShieldCheck, Award, Clock, Users, Phone, ChevronRight, Star } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const WHY = [
  { icon: ShieldCheck, title: "Licensed & insured", text: "Fully licensed, insured Edmonton-area contractor with permits and inspections handled for you." },
  { icon: Award, title: "Local craftsmanship", text: "A dedicated in-house team and trusted trades who take pride in every detail." },
  { icon: Clock, title: "On-schedule delivery", text: "Clear timelines and one point of contact from first consult to final walkthrough." },
  { icon: Users, title: "Neighbourhood knowledge", text: "We know the homes, permits and styles across the greater Edmonton area." },
];

function AreasIndex() {
  useReveal();
  return (
    <>
      <Seo
        title="Service Areas — Edmonton Home Renovations"
        description="Classic Interior Renovations serves Edmonton and surrounding communities including Sherwood Park, St. Albert, Spruce Grove, Leduc, Beaumont and southwest neighbourhoods like Glenora, Windermere, Terwillegar and Riverbend."
        path="/service-areas"
      />
      <section className="relative py-32 bg-[#0f2740] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20"><img src={HERO_IMG} alt="Edmonton home" className="w-full h-full object-cover" /></div>
        <div className="container-x relative z-10">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#bfe0f5] tracking-[0.26em] text-xs uppercase">Service Areas</span></div>
          <h1 className="font-display text-4xl sm:text-6xl font-bold mb-5 max-w-3xl">Renovations across the <span className="text-[#2f9fe6]">Edmonton area</span></h1>
          <p className="text-white/80 text-lg max-w-2xl">We proudly serve Edmonton and the surrounding communities. Find your area below to learn how we can help transform your home.</p>
        </div>
      </section>
      <section className="py-24 bg-white">
        <div className="container-x">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICE_AREAS.map((a, i) => (
              <Link key={a.slug} to={`/service-areas/${a.slug}`} className={`reveal delay-${(i % 3) + 1} group bg-[#f7f9fb] rounded-2xl p-7 border border-[#eef1f4] hover:border-[#2f9fe6] hover:shadow-lg transition-all`} data-testid={`area-link-${a.slug}`}>
                <div className="flex items-center gap-3 mb-2"><MapPin className="w-5 h-5 text-[#2f9fe6]" /><h2 className="font-display text-lg font-bold text-[#0f2740]">{a.name}</h2></div>
                <div className="text-xs text-[#9aa4af] uppercase tracking-wide mb-3">{a.label}</div>
                <p className="text-[#6b7683] text-sm line-clamp-3">{a.intro}</p>
                <span className="inline-flex items-center gap-1 text-[#2f9fe6] font-semibold text-sm mt-4 group-hover:gap-2 transition-all">Explore {a.name} <ArrowRight className="w-4 h-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default function ServiceArea() {
  const { slug } = useParams();
  useReveal();
  const { openEstimate } = useEstimate();
  const { data: reviews } = usePublicData("/reviews", TESTIMONIALS);
  useReveal([reviews]);

  if (!slug) return <AreasIndex />;

  const area = findArea(slug);
  if (!area) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <Helmet><meta name="robots" content="noindex" /></Helmet>
        <h1 className="font-display text-3xl font-bold text-[#0f2740] mb-3">Area not found</h1>
        <Link to="/service-areas" className="text-[#2f9fe6] font-semibold">View all service areas →</Link>
      </div>
    );
  }

  const title = `Home Renovations in ${area.name}`;
  const description = `${area.name} kitchen, bathroom, basement and full-home renovations by Classic Interior Renovations. Licensed, insured and trusted across ${area.label}. Free estimates.`;
  const nearbyAreas = (area.nearby || []).map(findArea).filter(Boolean);

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://classic-interior-renovations.com/" },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: "https://classic-interior-renovations.com/service-areas" },
      { "@type": "ListItem", position: 3, name: area.name, item: `https://classic-interior-renovations.com/service-areas/${area.slug}` },
    ],
  };

  return (
    <>
      <Seo title={title} description={description} path={`/service-areas/${area.slug}`} areaServed={`${area.name}, Alberta, Canada`} />
      <Helmet><script type="application/ld+json">{JSON.stringify(breadcrumb)}</script></Helmet>

      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt={`${area.name} home renovation`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2740]/95 via-[#0f2740]/85 to-[#0f2740]/50" />
        </div>
        <div className="container-x relative z-10 pt-28 pb-16">
          <nav className="flex items-center gap-2 text-sm text-[#bfe0f5] mb-5">
            <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="w-3.5 h-3.5" />
            <Link to="/service-areas" className="hover:text-white">Service Areas</Link><ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white">{area.name}</span>
          </nav>
          <div className="flex items-center gap-2 mb-4 text-[#2f9fe6]"><MapPin className="w-5 h-5" /><span className="tracking-[0.2em] text-xs uppercase">{area.label}</span></div>
          <h1 className="font-display text-white text-4xl sm:text-6xl font-bold leading-[1.05] mb-6 max-w-3xl">
            Home Renovations in <span className="text-[#2f9fe6]">{area.name}</span>
          </h1>
          <p className="text-white/85 text-lg max-w-2xl mb-9">{area.intro}</p>
          <div className="flex flex-wrap gap-4">
            <button data-testid="area-estimate-btn" onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold flex items-center gap-2 hover:bg-[#1f82c7] transition-all">Get a Free Estimate <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></button>
            <a href={COMPANY.phoneHref} className="px-8 py-4 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors flex items-center gap-2"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <div className="max-w-2xl mb-12 reveal">
            <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">What We Do</span></div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740]">Renovation services in {area.name}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {AREA_SERVICES.map((s, i) => (
              <Link key={s.to} to={s.to} className={`reveal delay-${(i % 3) + 1} group bg-[#f7f9fb] rounded-2xl p-7 border border-[#eef1f4] hover:border-[#2f9fe6] hover:shadow-lg transition-all`} data-testid={`area-service-${i}`}>
                <h3 className="font-display text-lg font-bold text-[#0f2740] mb-2 group-hover:text-[#2f9fe6] transition-colors">{s.title}</h3>
                <p className="text-[#6b7683] text-sm mb-4">{s.desc}</p>
                <span className="inline-flex items-center gap-1 text-[#2f9fe6] font-semibold text-sm group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-4 h-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY LOCAL */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x">
          <div className="max-w-2xl mb-12 reveal">
            <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Why Us</span></div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">Why {area.name} homeowners choose Classic Interior</h2>
            <p className="text-[#a9c6dd]">{area.note}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => (
              <div key={w.title} className={`reveal delay-${(i % 4) + 1} bg-white/5 rounded-2xl p-7 border border-white/10`}>
                <div className="w-12 h-12 rounded-xl bg-[#2f9fe6]/15 flex items-center justify-center mb-4"><w.icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                <h3 className="font-display text-lg font-semibold mb-2">{w.title}</h3>
                <p className="text-[#a9c6dd] text-sm leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews && reviews.length > 0 && (
        <section className="py-24 bg-white">
          <div className="container-x">
            <div className="max-w-2xl mb-12 reveal">
              <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">Reviews</span></div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740]">What our clients across the Edmonton area say</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((r, i) => (
                <div key={r.id || r.name} className={`reveal delay-${(i % 3) + 1} bg-[#f7f9fb] rounded-2xl p-7 border border-[#eef1f4]`} data-testid={`area-review-${i}`}>
                  <div className="flex mb-3">{[...Array(r.rating || 5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-[#2f9fe6] text-[#2f9fe6]" />)}</div>
                  <p className="text-[#4a5560] text-sm leading-relaxed mb-4">“{r.text}”</p>
                  <div className="font-display font-semibold text-[#0f2740]">{r.name}</div>
                  <div className="text-sm text-[#9aa4af]">{r.role}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NEARBY + CTA */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] mb-5">Ready to start your {area.name} renovation?</h2>
            <p className="text-[#5a6570] text-lg mb-8">Tell us about your home and goals. We'll arrange a consultation, discuss your vision and prepare a detailed, itemized estimate — with no pressure.</p>
            <button onClick={openEstimate} className="px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors inline-flex items-center gap-2" data-testid="area-cta-btn">Book a Free Consultation <ArrowRight className="w-5 h-5" /></button>
          </div>
          {nearbyAreas.length > 0 && (
            <div className="reveal-right bg-white rounded-2xl p-8 border border-[#eef1f4]">
              <h3 className="font-display font-semibold text-[#0f2740] mb-4">Nearby areas we serve</h3>
              <ul className="space-y-2">
                {nearbyAreas.map((n) => (
                  <li key={n.slug}>
                    <Link to={`/service-areas/${n.slug}`} className="flex items-center gap-2 text-[#4a5560] hover:text-[#2f9fe6] transition-colors"><MapPin className="w-4 h-4 text-[#2f9fe6]" /> Renovations in {n.name}</Link>
                  </li>
                ))}
                <li><Link to="/service-areas" className="flex items-center gap-2 text-[#2f9fe6] font-semibold mt-2">View all service areas <ArrowRight className="w-4 h-4" /></Link></li>
              </ul>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
